import * as React from "react"
import { jsonFetch } from "../bookingApi"
import { PRODUCT_CATALOG, communicateProductInUrl, productSlugFromSearch } from "../catalogue"
import type { HireBookingProps, Product } from "../types"
import type { useCheckoutAnalytics } from "./useCheckoutAnalytics"
import type { useCheckoutModel } from "./useCheckoutModel"
import type { useCheckoutState } from "./useCheckoutState"

type Inputs = Pick<ReturnType<typeof useCheckoutState>,
    "initialDraftRef" | "setActiveSlug" | "setProduct" | "setError" | "setPhase" | "activeSlug" | "abortController" | "productRetry" | "product" | "setSelectedAddonIds">
    & Pick<ReturnType<typeof useCheckoutModel>,
        "baseUrl">
    & Pick<ReturnType<typeof useCheckoutAnalytics>,
        "analytics">
    & { props: HireBookingProps }

export function useCheckoutProduct(inputs: Inputs) {
    const {
        props,
        initialDraftRef,
        setActiveSlug,
        setProduct,
        setError,
        setPhase,
        activeSlug,
        abortController,
        baseUrl,
        analytics,
        productRetry,
        product,
        setSelectedAddonIds,
    } = inputs
    React.useEffect(() => {
        const querySlug = typeof window === "undefined" ? "" : productSlugFromSearch(window.location.search)
        const configured = props.productSlug.trim()
        const restored = initialDraftRef.current?.productSlug || ""
        const preferred = props.productSlugSource === "property_first" ? configured || querySlug || restored : querySlug || configured || restored
        if (preferred && !PRODUCT_CATALOG.some((item) => item.slug === preferred)) {
            setActiveSlug("")
            setProduct(null)
            setError("The selected product is not available. Return to Arctic Hire to choose your equipment.")
            setPhase("ready")
            return
        }
        setActiveSlug(preferred || PRODUCT_CATALOG[0].slug)
    }, [props.productSlug, props.productSlugSource])

    React.useEffect(() => {
        if (activeSlug) communicateProductInUrl(activeSlug)
    }, [activeSlug])

    React.useEffect(() => {
        if (!activeSlug) return
        abortController.current?.abort()
        const controller = new AbortController()
        abortController.current = controller
        setPhase("loading")
        setError("")
        jsonFetch<{ product: Product }>(`${baseUrl}/v1/products/${encodeURIComponent(activeSlug)}`, { signal: controller.signal })
            .then((response) => {
                setProduct(response.product)
                setPhase("ready")
                analytics("hire_booking_viewed")
            })
            .catch((reason) => {
                if (reason.name !== "AbortError") { setProduct(null); setError(reason.message); setPhase("ready") }
            })
        return () => controller.abort()
    }, [activeSlug, baseUrl, productRetry])

    React.useEffect(() => {
        if (!product || product.slug !== activeSlug) return
        const availableIds = new Set(product.accessories.map((addon) => addon.id))
        setSelectedAddonIds((current) => {
            const compatible = current.filter((id) => availableIds.has(id))
            return compatible.length === current.length ? current : compatible
        })
    }, [activeSlug, product])
}
